import { Link } from 'react-router-dom';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import usePageMeta from '../hooks/usePageMeta';

const H2 = 'font-display text-2xl font-bold uppercase tracking-tight text-white';
const LABEL = 'font-mono text-[10px] uppercase tracking-[0.25em] text-accentText';
const BODY = 'text-sm leading-relaxed text-dim';

const TECH_TYPES = [
  {
    name: 'Strictly necessary technologies',
    text: 'These support essential website functions, such as security, form functionality and remembering privacy choices. Where permitted by applicable law, they operate without requiring consent.',
  },
  {
    name: 'Preference technologies',
    text: 'These remember choices you make, such as display or language preferences, to provide a more consistent browsing experience.',
  },
  {
    name: 'Analytics technologies',
    text: 'These help us understand how visitors use the website, including which pages they visit and whether they encounter errors. Depending on the tool, information collected may include device and browser details, IP addresses, referring pages and interactions with website content.',
  },
];

const SECTIONS = [
  {
    title: '1. What Are Cookies?',
    paragraphs: [
      'Cookies are small text files that websites place on your device. They can help a website function, remember your preferences and understand how visitors interact with its pages.',
      'Session cookies generally expire when you close your browser. Persistent cookies remain on your device until they expire or you delete them.',
      'Other technologies, such as browser storage, pixels and tags, may perform similar functions. This policy covers those technologies where applicable.',
    ],
  },
  {
    title: '3. Cookie Details',
    paragraphs: [
      'Some technologies may be set directly by RangeSafe, while others may be provided by companies supporting website functionality or analytics — currently Google Analytics, which runs only after you provide consent on the Cookie Preferences page. Their access to and use of information depends on the tools enabled and the applicable arrangements with those providers.',
    ],
  },
  {
    title: '4. Your Cookie Choices',
    paragraphs: [
      'Where consent is required by applicable law, nonessential technologies will be used only after you provide that consent. You may withdraw consent through the available controls. Withdrawal does not affect processing that lawfully occurred before withdrawal.',
      'You can also use your browser settings to block or delete cookies. Instructions are generally available through your browser’s help or privacy settings.',
      'Blocking certain technologies may affect website functionality. Deleting cookies may remove saved preferences, including previously recorded cookie choices. Browser cookie settings may not control every type of similar technology.',
    ],
  },
  {
    title: '5. Links to Other Websites',
    paragraphs: [
      'Our commercial website links to RangeSafe’s retail customer website. That website may use different cookies and technologies and is subject to its own privacy and cookie notices. Choices made on one website may not automatically apply to the other.',
    ],
  },
  {
    title: '6. Updates to This Policy',
    paragraphs: [
      'We may update this Cookie Policy to reflect changes in website technologies, our practices or applicable requirements. The revised policy will be posted on this page with an updated date. Where required, we will provide additional notice or request renewed consent.',
    ],
  },
];

export default function CookiePolicy() {
  usePageMeta(
    'Cookie Policy | RangeSafe Commercial Solutions',
    'How RangeSafe, LLC uses cookies and similar technologies on the RangeSafe commercial website.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Legal"
          lines={['Cookie Policy']}
          lead="This Cookie Policy explains how RangeSafe, LLC (“RangeSafe,” “we,” “us” or “our”) uses cookies and similar technologies on our commercial website. It should be read alongside our Privacy Policy."
        />
        <p className={`mt-6 ${LABEL}`} data-testid="cookie-policy-last-updated">
          Cookie Policy · Last updated: October 26, 2026
        </p>
      </header>

      <div className="mx-auto max-w-shell space-y-12 px-5 pb-24 sm:px-8">
        <Reveal>
          <section className="max-w-3xl" data-testid="cookie-section-1">
            <h2 className={H2}>{SECTIONS[0].title}</h2>
            {SECTIONS[0].paragraphs.map((p) => (
              <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                {p}
              </p>
            ))}
          </section>
        </Reveal>

        <Reveal>
          <section className="max-w-3xl" data-testid="cookie-section-2">
            <h2 className={H2}>2. Technologies Used on This Website</h2>
            <div className="mt-4 space-y-5">
              {TECH_TYPES.map((t) => (
                <div key={t.name}>
                  <p className={LABEL}>{t.name}</p>
                  <p className={`mt-2 ${BODY}`}>{t.text}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {SECTIONS.slice(1).map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.03, 0.12)}>
            <section className="max-w-3xl" data-testid={`cookie-section-${i + 3}`}>
              <h2 className={H2}>{s.title}</h2>
              {s.paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                  {p}
                </p>
              ))}
            </section>
          </Reveal>
        ))}

        <Reveal>
          <section className="border border-line bg-panel/60 p-8 sm:p-10" data-testid="cookie-policy-contact">
            <p className={LABEL}>7. Contact Us</p>
            <p className={`mt-4 max-w-2xl ${BODY}`}>
              For questions about this Cookie Policy, please contact:
            </p>
            <div className="mt-4 space-y-1 text-sm leading-relaxed text-dim">
              <p className="text-white">RangeSafe, LLC</p>
              <p>Email: privacy@rangesafe.com</p>
              <p>Mailing address: 4583 Highway 903, Bracey, Virginia 23919</p>
            </div>
            <p className="mt-6">
              <Link to="/cookie-preferences" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white underline decoration-line underline-offset-4 transition-colors hover:decoration-accent" data-testid="cookie-policy-preferences-link">
                Manage your cookie preferences
              </Link>
            </p>
          </section>
        </Reveal>
      </div>
    </main>
  );
}
